import React, { Component, type ComponentType, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

// Webpack reports failed chunk requests as "Loading chunk N failed" (name
// ChunkLoadError); Chromium/Firefox phrase native import() failures as
// "Failed to fetch dynamically imported module", Safari as "Importing a module
// script failed". React.lazy caches a rejected import permanently, so for
// these errors an in-place retry can only rethrow — the fallback hides
// "Try again" and offers only Reload. (A bare "Failed to fetch" is left out
// on purpose: too generic to safely classify as a chunk failure.)
const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk|dynamically imported module|Importing a module script failed/i.test(
    error.message,
  );

const ChunkErrorContent: React.FC<{ showRetry: boolean; onRetry: () => void }> = ({
  showRetry,
  onRetry,
}) => {
  const { t } = useTranslation('common');
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography color="text.secondary">
        {t('chunkError.message', { defaultValue: 'This section failed to load.' })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {showRetry && (
          <Button variant="outlined" onClick={onRetry}>
            {t('buttons.retry')}
          </Button>
        )}
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t('chunkError.reload', { defaultValue: 'Reload page' })}
        </Button>
      </Box>
    </Box>
  );
};

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  // When this value changes while an error is showing, the error STATE is
  // cleared — the children themselves are never remounted (a `key` on the
  // boundary would remount healthy chrome on every navigation).
  resetKey?: unknown;
  // Viewport-sized error card for boundaries with no chrome around them (the
  // App-level layout boundary). Default is a content-well card (minHeight 240,
  // matching RouteLoadingFallback).
  fullPage?: boolean;
  // Custom shell around the error content (e.g. the chat-dock frame).
  errorWrapper?: ComponentType<{ children: ReactNode }>;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
}

export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ChunkErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const content = (
      <ChunkErrorContent showRetry={!isChunkLoadError(error)} onRetry={this.handleRetry} />
    );
    const Wrapper = this.props.errorWrapper;
    if (Wrapper) return <Wrapper>{content}</Wrapper>;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          ...(this.props.fullPage
            ? { minHeight: '100vh', bgcolor: 'background.default' }
            : { minHeight: 240 }),
        }}
      >
        {content}
      </Box>
    );
  }
}

// Route-level variant: clears a shown error when the pathname changes, so
// navigating away from a broken route renders normally without a reload.
export const RoutedChunkErrorBoundary: React.FC<{
  children: ReactNode;
  fullPage?: boolean;
}> = ({ children, fullPage }) => {
  const location = useLocation();
  return (
    <ChunkErrorBoundary resetKey={location.pathname} fullPage={fullPage}>
      {children}
    </ChunkErrorBoundary>
  );
};

export default ChunkErrorBoundary;
